import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Building2, Users, ChevronRight, HandCoins, Landmark, ShoppingBag, Trophy } from 'lucide-react';
import SuivezNous from '../components/SuivezNous';
import { fetchEquipesPubliques } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';

export default function Home() {
  // Le logo choisi par le comité, et non le fichier livré avec l'application :
  // l'en-tête du site suivait le changement, cette bannière restait à l'ancien.
  const perso = usePersonnalisation();

  // Les equipes ne s'affichent que si le club l'a voulu : la reponse est
  // legere et publique, la carte s'ajoute ou s'efface d'elle-meme.
  const { data: equipes } = useQuery({
    queryKey: ['equipes-publiques'],
    queryFn: fetchEquipesPubliques,
    retry: false,
  });

  return (
    <div className="p-4 lg:p-0 space-y-6 lg:space-y-8 pb-24 lg:pb-0">
      {/* Section héro */}
      <section className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800 text-white p-6 lg:p-10 shadow-lg">
        <div className="flex items-center gap-4 mb-4">
          <img
            src={perso?.logoUrl ?? '/logo-par-defaut.png'}
            alt="Logo de l'association"
            className="w-16 h-16 lg:w-24 lg:h-24 rounded-full bg-white p-1 shadow-lg object-contain"
          />
          <div>
            <h1 className="text-3xl lg:text-5xl font-bold leading-tight">
              Bienvenue — {perso?.nomAssociation ?? 'notre association'}
            </h1>
            {perso?.sousTitre && (
              <p className="mt-1 lg:mt-2 lg:text-lg text-primary-100">{perso.sousTitre}</p>
            )}
          </div>
        </div>
        <p className="mt-3 lg:mt-4 lg:text-lg text-primary-100 max-w-2xl">
          Fête annuelle, concerts, repas et animations.
        </p>
        <Link
          to="/evenements"
          className="mt-5 lg:mt-6 inline-flex items-center gap-1 text-sm lg:text-base font-semibold bg-white text-primary-700 px-4 py-2 rounded-full"
        >
          Voir les événements
          <ChevronRight className="w-4 h-4" />
        </Link>
      </section>

      {/* Accès rapides */}
      <section className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        <Link
          to="/evenements"
          className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
        >
          <div className="p-3 bg-primary-50 rounded-full text-primary-600">
            <Calendar className="w-6 h-6" />
          </div>
          <span className="font-medium text-sm">Événements</span>
        </Link>

        <Link
          to="/nos-sponsors"
          className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
        >
          <div className="p-3 bg-purple-50 rounded-full text-purple-600">
            <Building2 className="w-6 h-6" />
          </div>
          <span className="font-medium text-sm">Sponsors</span>
        </Link>

        <Link
          to="/association"
          className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
        >
          <div className="p-3 bg-amber-50 rounded-full text-amber-600">
            <Landmark className="w-6 h-6" />
          </div>
          <span className="font-medium text-sm">Association</span>
        </Link>

        <Link
          to="/don-libre"
          className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
        >
          <div className="p-3 bg-red-50 rounded-full text-red-600">
            <HandCoins className="w-6 h-6" />
          </div>
          <span className="font-medium text-sm">Don libre</span>
        </Link>

        <Link
          to="/informations"
          className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
        >
          <div className="p-3 bg-blue-50 rounded-full text-blue-600">
            <Users className="w-6 h-6" />
          </div>
          <span className="font-medium text-sm">Espace membres</span>
        </Link>

        {/* Les équipes : le club décide de les publier ou non, et la carte
            suit cette décision. Un parent qui cherche celle de son enfant
            arrive par là. */}
        {equipes?.ouverte && (
          <Link
            to="/nos-equipes"
            className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
          >
            <div className="p-3 bg-teal-50 rounded-full text-teal-600">
              <Trophy className="w-6 h-6" />
            </div>
            <span className="font-medium text-sm">{equipes.titre ?? 'Nos équipes'}</span>
          </Link>
        )}

        {/* La boutique n'apparaît que si l'option est souscrite : sous
            « Association », à droite d'« Espace membres » sur trois colonnes. */}
        {perso?.boutiqueActive && (
          <Link
            to="/boutique"
            className="card flex flex-col items-center text-center gap-2 hover:shadow-md transition"
          >
            <div className="p-3 bg-emerald-50 rounded-full text-emerald-600">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <span className="font-medium text-sm">Boutique</span>
          </Link>
        )}
      </section>

      <SuivezNous />
    </div>
  );
}
